import { z } from 'zod';

/**
 * Enquiry form validation (§39). These schemas mirror the checks in
 * `submit_website_enquiry`; the database is the one that decides, and this is
 * here so the visitor is told before they submit.
 */

const digits = (value: string) => value.replace(/\D/g, '').length;

export const fullNameSchema = z
  .string()
  .trim()
  .min(2, 'Please enter your name.')
  .max(120, 'That name is too long.');

export const phoneSchema = z
  .string()
  .trim()
  .min(1, 'Please enter your WhatsApp number.')
  .refine(
    (v) => digits(v) >= 7 && digits(v) <= 15,
    'Enter a valid number, including the country code.',
  );

/** Email addresses on public enquiry forms are required and validated here. */
export const requiredEmailSchema = z
  .string()
  .trim()
  .min(1, 'Please enter your email address.')
  .email('Enter a valid email address.');

const optionalText = (max: number, message: string) =>
  z.union([z.literal(''), z.string().trim().max(max, message)]);

/** Every form carries the same trap field. */
const honeypot = { companyWebsite: z.string().optional() };

/** §27 — the six-field quotation form, plus the optional detail fields from §22. */
export const quoteFormSchema = z.object({
  fullName: fullNameSchema,
  phone: phoneSchema,
  email: requiredEmailSchema,
  carWanted: z.string().trim().min(2, 'Tell us which car you want.').max(120, 'That is too long.'),
  budget: optionalText(60, 'That is too long.'),
  location: optionalText(120, 'That is too long.'),
  // Revealed by "Add more details" (§22).
  year: optionalText(20, 'That is too long.'),
  preferredSource: optionalText(60, 'That is too long.'),
  purchaseTime: optionalText(40, 'That is too long.'),
  colour: optionalText(60, 'That is too long.'),
  message: optionalText(2000, 'Please keep your message under 2000 characters.'),
  ...honeypot,
});

export type QuoteFormValues = z.infer<typeof quoteFormSchema>;

export const quoteFormDefaults: QuoteFormValues = {
  fullName: '',
  phone: '',
  email: '',
  carWanted: '',
  budget: '',
  location: '',
  year: '',
  preferredSource: '',
  purchaseTime: '',
  colour: '',
  message: '',
  companyWebsite: '',
};

/**
 * The short form on the home page. Four fields is the most that can be asked
 * of someone who came to look at cars, so it collects only what the team needs
 * to call back; everything else is asked on the phone.
 */
export const homeLeadSchema = z.object({
  fullName: fullNameSchema,
  phone: phoneSchema,
  carWanted: z.string().trim().min(2, 'Tell us which car you want.').max(120, 'That is too long.'),
  budget: optionalText(60, 'That is too long.'),
  ...honeypot,
});

export type HomeLeadValues = z.infer<typeof homeLeadSchema>;

export const homeLeadDefaults: HomeLeadValues = {
  fullName: '',
  phone: '',
  carWanted: '',
  budget: '',
  companyWebsite: '',
};

/** §23 — "Can't find your dream car? We'll find it for you." */
export const importFormSchema = z.object({
  fullName: fullNameSchema,
  phone: phoneSchema,
  email: requiredEmailSchema,
  make: z.string().trim().min(1, 'Which make are you looking for?').max(60, 'That is too long.'),
  model: optionalText(60, 'That is too long.'),
  year: optionalText(20, 'That is too long.'),
  budget: optionalText(60, 'That is too long.'),
  fuel: optionalText(30, 'That is too long.'),
  transmission: optionalText(30, 'That is too long.'),
  colour: optionalText(60, 'That is too long.'),
  country: optionalText(60, 'That is too long.'),
  location: optionalText(120, 'That is too long.'),
  message: optionalText(2000, 'Please keep your message under 2000 characters.'),
  ...honeypot,
});

export type ImportFormValues = z.infer<typeof importFormSchema>;

export const importFormDefaults: ImportFormValues = {
  fullName: '',
  phone: '',
  email: '',
  make: '',
  model: '',
  year: '',
  budget: '',
  fuel: '',
  transmission: '',
  colour: '',
  country: '',
  location: '',
  message: '',
  companyWebsite: '',
};

/**
 * §32 — the import-advice request (`/free-import-advice`).
 *
 * Five questions, four of them required. It was eight, and the three that went
 * were the three that made it read as a booking the site cannot honour: a
 * preferred date, a preferred time, and where the visitor was travelling from.
 * The site has no calendar and invents no availability (A16), so asking
 * someone to pick a slot was asking them to choose from something that did not
 * exist — and `meetingType` already carries the only scheduling fact the team
 * acts on.
 *
 * Email is required on public enquiry forms that ask for it. This form needs
 * an address for the written summary even when `meetingType` says the reply
 * should come by phone or WhatsApp.
 *
 * `message` stays optional. It is the field that makes the request useful, but
 * a required free-text box is the single biggest drop-off on a short form, and
 * the team can ask on the call.
 */
export const consultationFormSchema = z.object({
  fullName: fullNameSchema,
  phone: phoneSchema,
  email: requiredEmailSchema,
  meetingType: z.string().trim().min(1, 'Choose how we should reach you.'),
  message: optionalText(2000, 'Please keep your message under 2000 characters.'),
  ...honeypot,
});

export type ConsultationFormValues = z.infer<typeof consultationFormSchema>;

export const consultationFormDefaults: ConsultationFormValues = {
  fullName: '',
  phone: '',
  email: '',
  meetingType: '',
  message: '',
  companyWebsite: '',
};

/**
 * How the team should come back to the visitor.
 *
 * The values are unchanged, so every enquiry already in the database still
 * reads the same way. The labels are phrased as a reply rather than as a
 * meeting: the form no longer arranges a slot, it asks where to send the
 * answer, and "Office meeting in Kampala" promised a diary entry that nothing
 * behind the form could make.
 */
export const meetingTypes = [
  { value: 'whatsapp', label: 'WhatsApp message' },
  { value: 'phone', label: 'Phone call' },
  { value: 'video', label: 'Video call' },
  { value: 'office', label: 'In person, at our Kampala office' },
];

export const purchaseTimes = [
  { value: 'immediately', label: 'Immediately' },
  { value: '1-3 months', label: 'In 1–3 months' },
];

/**
 * The organisation enquiry on /clients.
 *
 * It is a separate schema rather than a reuse of `quoteFormSchema` because the
 * reader is a different one. The quotation form asks an individual which car
 * they want; this asks someone spending an organisation's money, and the two
 * answers the team cannot work without — which organisation, and how many
 * vehicles — are not fields that form has. A fleet request that arrives
 * without the organisation's name is a request nobody can price.
 *
 * Five required fields, which is two more than the home page's short form and
 * deliberately so: the ask here is larger, the sender is at work rather than
 * browsing, and an email address is what a written quotation is sent to.
 *
 * `sector` and `fleetSize` are pickers rather than free text so the leads
 * arrive in the same vocabulary the roster is grouped by — a sector typed
 * fifteen different ways cannot be counted later. Both are optional: a
 * visitor whose organisation does not fit our fifteen groups must not be
 * stopped at a dropdown.
 */
export const fleetEnquirySchema = z.object({
  organisation: z
    .string()
    .trim()
    .min(2, 'Which organisation are you buying for?')
    .max(160, 'That name is too long.'),
  sector: optionalText(60, 'That is too long.'),
  vehiclesNeeded: z
    .string()
    .trim()
    .min(2, 'Tell us which vehicles you need.')
    .max(160, 'That is too long.'),
  fleetSize: optionalText(40, 'That is too long.'),
  fullName: fullNameSchema,
  phone: phoneSchema,
  email: requiredEmailSchema,
  message: optionalText(2000, 'Please keep your message under 2000 characters.'),
  ...honeypot,
});

export type FleetEnquiryValues = z.infer<typeof fleetEnquirySchema>;

export const fleetEnquiryDefaults: FleetEnquiryValues = {
  organisation: '',
  sector: '',
  vehiclesNeeded: '',
  fleetSize: '',
  fullName: '',
  phone: '',
  email: '',
  message: '',
  companyWebsite: '',
};

/**
 * How many vehicles the request is for.
 *
 * Bands rather than a number field: a procurement officer at the quotation
 * stage usually knows the shape of the order and not its final count, and a
 * required integer invites a made-up one. The values are stored as written, so
 * they read the same in the enquiry queue as they did on the form.
 */
export const fleetSizes = [
  { value: 'One vehicle', label: 'One vehicle' },
  { value: '2–5 vehicles', label: '2–5 vehicles' },
  { value: '6–15 vehicles', label: '6–15 vehicles' },
  { value: '16 or more vehicles', label: '16 or more vehicles' },
];
/** International format as composed by the phone field: "+" then 7–15 digits. */
const internationalPhoneSchema = z
  .string()
  .trim()
  .min(1, 'Please enter your WhatsApp number.')
  .regex(/^\+[1-9]\d{6,14}$/, 'Enter a valid number for the selected country.');

/** § accessory order — full name, phone, email (revamped flow). */
export const accessoryOrderFormSchema = z.object({
  fullName: fullNameSchema,
  phone: internationalPhoneSchema,
  email: requiredEmailSchema,
  message: optionalText(2000, 'Please keep your message under 2000 characters.'),
  ...honeypot,
});

export type AccessoryOrderFormValues = z.infer<typeof accessoryOrderFormSchema>;

export const accessoryOrderFormDefaults: AccessoryOrderFormValues = {
  fullName: '',
  phone: '',
  email: '',
  message: '',
  companyWebsite: '',
};

/** § car order — same fields as the accessory order. */
export const carOrderFormSchema = accessoryOrderFormSchema;
export type CarOrderFormValues = AccessoryOrderFormValues;
export const carOrderFormDefaults: CarOrderFormValues = accessoryOrderFormDefaults;
